import { useRef, useState, type DragEvent } from 'react';
import {
  AlertTriangle,
  ArrowUpToLine,
  FileSpreadsheet,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils/cn';
import { cantidadDe, formatoCantidad } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { tradeService } from '@/features/trade/services/trade-service';
import type { ProformaExtraction } from '@/features/trade/types/trade.types';
import type { DatosDeProducto, PartidaDeProducto } from '../../types/comercia.types';
import { COLORES_DE_CARGA, usd } from '../../utils/formatos';
import { BuscadorDePartida } from './buscador-de-partida';

/** Una línea de la proforma mientras se revisa. Los números quedan como texto hasta confirmar. */
interface Linea {
  clave: number;
  descripcion: string;
  cantidad: string;
  fob: string;
  largo: string;
  ancho: string;
  alto: string;
  peso: string;
  partida: PartidaDeProducto | null;
  /** La partida que traía el documento, si no se encontró en el arancel. */
  codigoLeido: string | null;
  dudosa: boolean;
}

const aTexto = (n: number | null | undefined) => (n == null ? '' : String(n).replace('.', ','));
const aNumero = (s: string) => {
  const n = Number(s.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

function lineasDe(x: ProformaExtraction): Linea[] {
  return x.lines.map((l, i) => ({
    clave: i,
    descripcion: l.description,
    cantidad: aTexto(l.quantity),
    fob: aTexto(l.unitPrice),
    largo: aTexto(l.lengthCm),
    ancho: aTexto(l.widthCm),
    alto: aTexto(l.heightCm),
    peso: aTexto(l.unitWeightKg),
    partida:
      l.tariffHeadingId && l.hsCode
        ? {
            id: l.tariffHeadingId,
            codigo: l.hsCode,
            descripcion: l.hsDescription ?? '',
            ga: l.gaRate ?? 0,
          }
        : null,
    codigoLeido: !l.tariffHeadingId ? (l.hsCodeRaw ?? null) : null,
    dudosa: l.confidence < 0.7,
  }));
}

/** Qué le falta a una línea para poder entrar (las medidas y el peso hacen falta para la carga). */
function faltaDe(l: Linea): string | null {
  if (l.descripcion.trim().length < 2) return 'descripción';
  const c = aNumero(l.cantidad);
  if (!(c > 0) || !Number.isInteger(c)) return 'cantidad';
  if (!(aNumero(l.fob) > 0)) return 'precio';
  if (!(aNumero(l.largo) > 0) || !(aNumero(l.ancho) > 0) || !(aNumero(l.alto) > 0))
    return 'medidas';
  if (!(aNumero(l.peso) > 0)) return 'peso';
  return null;
}

/**
 * «Subir proforma» (maqueta de Melanie): 1 · sube la proforma tal como te la mandó
 * el proveedor, 2 · revisa lo que leyó y corrige, 3 · confirma y los productos se
 * agregan a la lista.
 *
 * El Excel se lee sin IA; el PDF y la foto necesitan la IA configurada en el
 * servidor (si no está, el aviso lo dice y sugiere el Excel).
 */
export function SubirProforma({
  coloresUsados,
  yaEnLaLista = [],
  onConfirmar,
}: {
  coloresUsados: string[];
  /** Las descripciones de los productos que ya están en la importación. */
  yaEnLaLista?: string[];
  onConfirmar: (lineas: DatosDeProducto[]) => Promise<void>;
}) {
  const [leyendo, setLeyendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lectura, setLectura] = useState<ProformaExtraction | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [proveedor, setProveedor] = useState('');
  const [arrastrando, setArrastrando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [eligiendoPartida, setEligiendoPartida] = useState<number | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const leer = async (archivo: File) => {
    setLeyendo(true);
    setError(null);
    try {
      const r = await tradeService.extractProforma(archivo);
      if (r.lines.length === 0) {
        setError('No se encontraron productos en el archivo. Revisa que sea la proforma.');
        return;
      }
      setLectura(r);
      setLineas(lineasDe(r));
      setProveedor(r.supplierName ?? '');
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo leer la proforma. Prueba con el Excel.'));
    } finally {
      setLeyendo(false);
    }
  };

  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setArrastrando(false);
    const archivo = e.dataTransfer.files?.[0];
    if (archivo) void leer(archivo);
  };

  const cambiar = (clave: number, campo: keyof Linea, valor: string) =>
    setLineas((ls) =>
      ls.map((l) => (l.clave === clave ? { ...l, [campo]: valor, dudosa: false } : l))
    );

  const incompletas = lineas.filter((l) => faltaDe(l) !== null).length;
  const sinPartida = lineas.filter((l) => !l.partida).length;
  // Sólo las líneas con cantidad y precio válidos: una cantidad negativa restaba.
  const total = lineas.reduce((s, l) => {
    const c = aNumero(l.cantidad);
    const f = aNumero(l.fob);
    return s + (c > 0 && f > 0 ? c * f : 0);
  }, 0);
  /*
    Subir dos veces la misma proforma duplicaba todo sin aviso. No se impide —puede
    ser otra partida de la misma mercadería—, pero se dice cuáles ya están.
  */
  const enLaLista = new Set(yaEnLaLista.map((d) => d.trim().toLowerCase()));
  const repetidas = lineas.filter((l) => enLaLista.has(l.descripcion.trim().toLowerCase()));

  const confirmar = async () => {
    if (incompletas > 0 || !proveedor.trim()) return;
    setGuardando(true);
    setError(null);
    try {
      // Cada producto con un color distinto de los que ya hay en el plano.
      const libres = COLORES_DE_CARGA.filter(
        (c) => !coloresUsados.some((u) => u.toLowerCase() === c.toLowerCase())
      );
      const paleta = libres.length > 0 ? libres : [...COLORES_DE_CARGA];
      await onConfirmar(
        lineas.map((l, i) => ({
          descripcion: l.descripcion.trim(),
          proveedor: proveedor.trim(),
          partidaId: l.partida?.id ?? null,
          cantidad: aNumero(l.cantidad),
          unidad: 'UND',
          largoCm: aNumero(l.largo),
          anchoCm: aNumero(l.ancho),
          altoCm: aNumero(l.alto),
          pesoKg: aNumero(l.peso),
          fobUnitario: aNumero(l.fob),
          color: paleta[i % paleta.length],
          fragil: false,
        }))
      );
      setLectura(null);
      setLineas([]);
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudieron agregar los productos. Vuelve a intentar.'));
    } finally {
      setGuardando(false);
    }
  };

  const paso = lectura ? 2 : 1;

  return (
    <div className="space-y-5">
      {!lectura ? (
        <section className="rounded-tarjeta border bg-card p-6 shadow-sm">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setArrastrando(true);
            }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={soltar}
            className={cn(
              'flex flex-col items-center rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-colors',
              arrastrando ? 'border-orange-700 bg-orange-700/5' : 'border-border'
            )}
          >
            <span className="grid h-16 w-16 place-items-center rounded-2xl bg-orange-700 text-white shadow-md">
              {leyendo ? (
                <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowUpToLine className="h-7 w-7" aria-hidden="true" />
              )}
            </span>
            <p className="mt-5 font-titulo text-xl font-semibold">
              {leyendo ? 'Leyendo la proforma…' : 'Arrastra aquí la proforma del proveedor'}
            </p>
            <p className="mt-1.5 text-[15px] text-muted-foreground">
              PDF, Excel o foto · Prixia lee los productos, cantidades y precios por ti
            </p>
            <Button
              type="button"
              variant="degradado"
              className="mt-6 rounded-full px-8"
              size="lg"
              disabled={leyendo}
              onClick={() => entrada.current?.click()}
            >
              <ArrowUpToLine className="h-4 w-4" aria-hidden="true" />
              Elegir archivo
            </Button>
            <input
              ref={entrada}
              type="file"
              className="sr-only"
              accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp"
              aria-label="Elegir la proforma"
              onChange={(e) => {
                const archivo = e.target.files?.[0];
                if (archivo) void leer(archivo);
                e.target.value = '';
              }}
            />
          </div>
          {error && (
            <p
              role="alert"
              className="mt-4 rounded-xl bg-bloqueo-suave px-4 py-3 text-sm text-bloqueo"
            >
              {error}
            </p>
          )}
          <PasosDeLaProforma actual={paso} />
        </section>
      ) : (
        <section className="space-y-4 rounded-tarjeta border bg-card p-6 shadow-sm">
          <PasosDeLaProforma actual={paso} arriba />
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="flex items-center gap-2 font-titulo text-xl font-semibold">
                <FileSpreadsheet className="h-5 w-5 text-orange-700" aria-hidden="true" />
                {lectura.fileName}
              </h2>
              <p className="text-sm text-muted-foreground">
                {cantidadDe(lineas.length, 'producto leído', 'productos leídos')}
                {lectura.proformaNumber ? ` · proforma ${lectura.proformaNumber}` : ''}
                {lectura.usedAi ? ' · leída con IA: revísala con cuidado' : ''} · {usd(total)} FOB
              </p>
            </div>
            <label className="w-full max-w-xs text-sm">
              <span className="mb-1 block font-semibold">
                Proveedor <span className="text-destructive">*</span>
              </span>
              <input
                value={proveedor}
                onChange={(e) => setProveedor(e.target.value)}
                placeholder="Quién te vende esto"
                className={cn(
                  'h-11 w-full rounded-xl border bg-campo px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  proveedor.trim() ? 'border-input' : 'border-destructive'
                )}
              />
            </label>
          </div>

          {repetidas.length > 0 && (
            <p
              role="status"
              className="flex gap-2 rounded-xl bg-alerta-suave px-4 py-3 text-sm text-alerta"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>
                {repetidas.length === 1
                  ? '1 producto de esta proforma ya está en tu lista'
                  : `${repetidas.length} productos de esta proforma ya están en tu lista`}
                : {repetidas.map((l) => l.descripcion.trim()).join(', ')}. Si los agregas quedan
                como líneas aparte; quítalos acá si es la misma mercadería.
              </span>
            </p>
          )}

          {lectura.warnings.length > 0 && (
            <ul className="space-y-1 rounded-xl bg-alerta-suave px-4 py-3 text-sm text-alerta">
              {lectura.warnings.map((w) => (
                <li key={w} className="flex gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {w}
                </li>
              ))}
            </ul>
          )}

          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[1100px] text-sm">
              <thead className="bg-surface-sunken text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2.5 text-left font-semibold">
                    Producto
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-semibold">
                    Cantidad
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-semibold">
                    FOB / u. (USD)
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-center font-semibold" colSpan={3}>
                    Largo × ancho × alto (cm)
                  </th>
                  <th scope="col" className="px-2 py-2.5 text-right font-semibold">
                    Peso / u. (kg)
                  </th>
                  <th scope="col" className="px-3 py-2.5 text-left font-semibold">
                    Partida
                  </th>
                  <th scope="col" className="w-10 px-2 py-2.5">
                    <span className="sr-only">Quitar</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lineas.map((l) => {
                  const falta = faltaDe(l);
                  return (
                    <tr key={l.clave} className={cn(l.dudosa && 'bg-alerta-suave/60')}>
                      <td className="px-3 py-2">
                        <CeldaTexto
                          valor={l.descripcion}
                          etiqueta="Producto"
                          onCambio={(v) => cambiar(l.clave, 'descripcion', v)}
                          ancho="w-full min-w-[220px]"
                        />
                        {falta && <p className="mt-1 text-xs text-bloqueo">Falta: {falta}</p>}
                        {enLaLista.has(l.descripcion.trim().toLowerCase()) && (
                          <p className="mt-1 text-xs text-alerta">Ya está en tu lista</p>
                        )}
                      </td>
                      {(['cantidad', 'fob'] as const).map((c) => (
                        <td key={c} className="px-2 py-2">
                          <CeldaTexto
                            valor={l[c]}
                            etiqueta={c === 'cantidad' ? 'Cantidad' : 'FOB por unidad'}
                            onCambio={(v) => cambiar(l.clave, c, v)}
                            numero
                          />
                        </td>
                      ))}
                      {(['largo', 'ancho', 'alto'] as const).map((c) => (
                        <td key={c} className="px-1 py-2">
                          <CeldaTexto
                            valor={l[c]}
                            etiqueta={c}
                            onCambio={(v) => cambiar(l.clave, c, v)}
                            numero
                            ancho="w-16"
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2">
                        <CeldaTexto
                          valor={l.peso}
                          etiqueta="Peso por unidad"
                          onCambio={(v) => cambiar(l.clave, 'peso', v)}
                          numero
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setEligiendoPartida(l.clave)}
                          className={cn(
                            'inline-flex max-w-[200px] items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            l.partida ? 'font-mono font-semibold' : 'text-alerta'
                          )}
                        >
                          {l.partida ? (
                            l.partida.codigo
                          ) : (
                            <>
                              <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                              {l.codigoLeido ? `${l.codigoLeido}: elegir` : 'Elegir partida'}
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}
                          className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-bloqueo-suave"
                          aria-label={`Quitar ${l.descripcion}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {error && (
            <p role="alert" className="rounded-xl bg-bloqueo-suave px-4 py-3 text-sm text-bloqueo">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {incompletas > 0
                ? `${cantidadDe(incompletas, 'línea necesita', 'líneas necesitan')} datos: las medidas y el peso hacen falta para acomodar la carga.`
                : sinPartida > 0
                  ? `${cantidadDe(sinPartida, 'producto entra', 'productos entran')} sin partida: se la puedes poner después, desde la lista.`
                  : 'Todo listo para agregar.'}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLectura(null);
                  setLineas([]);
                }}
                disabled={guardando}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                Descartar
              </Button>
              <Button
                type="button"
                variant="degradado"
                className="px-6"
                disabled={guardando || incompletas > 0 || !proveedor.trim() || lineas.length === 0}
                onClick={() => void confirmar()}
              >
                {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Agregar {formatoCantidad(lineas.length)}{' '}
                {lineas.length === 1 ? 'producto' : 'productos'}
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog
        open={eligiendoPartida !== null}
        onOpenChange={(o) => !o && setEligiendoPartida(null)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Partida arancelaria</DialogTitle>
            <DialogDescription>
              {lineas.find((l) => l.clave === eligiendoPartida)?.descripcion}
            </DialogDescription>
          </DialogHeader>
          <BuscadorDePartida
            enLinea
            valor={lineas.find((l) => l.clave === eligiendoPartida)?.partida ?? null}
            onCambio={(p) => {
              // Elegir la partida es revisar la línea: deja de resaltarse.
              setLineas((ls) =>
                ls.map((l) =>
                  l.clave === eligiendoPartida
                    ? { ...l, partida: p, dudosa: p ? false : l.dudosa }
                    : l
                )
              );
              if (p) setEligiendoPartida(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CeldaTexto({
  valor,
  etiqueta,
  onCambio,
  numero,
  ancho = 'w-24',
}: {
  valor: string;
  etiqueta: string;
  onCambio: (v: string) => void;
  numero?: boolean;
  ancho?: string;
}) {
  return (
    <input
      value={valor}
      aria-label={etiqueta}
      inputMode={numero ? 'decimal' : undefined}
      onChange={(e) => onCambio(e.target.value)}
      className={cn(
        'h-9 rounded-lg border border-input bg-campo px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        numero && 'text-right tabular-nums',
        ancho
      )}
    />
  );
}

function PasosDeLaProforma({ actual, arriba }: { actual: 1 | 2; arriba?: boolean }) {
  const pasos = [
    ['Sube la proforma', 'Tal como te la mandó el proveedor'],
    ['Revisa lo que leyó', 'Corrige lo que haga falta'],
    ['Confirma', 'Los productos se agregan a la lista'],
  ];
  return (
    <ol className={cn('grid gap-4 sm:grid-cols-3', arriba ? 'mb-2' : 'mt-6')}>
      {pasos.map(([titulo, texto], i) => (
        <li key={titulo} className="flex items-center gap-3">
          <span
            className={cn(
              'grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold',
              i + 1 === actual ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
            )}
          >
            {i + 1}
          </span>
          <span>
            <span className="block text-[15px] font-semibold">{titulo}</span>
            <span className="block text-sm text-muted-foreground">{texto}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}
